import { Switch as ChakraSwitch } from '@chakra-ui/react'
import * as React from 'react'

const toggleSizes = {
  xs: { width: '24px', height: '12px', thumb: '12px', checkedLeft: '0px' },
  sm: { width: '32px', height: '16px', thumb: '16px', checkedLeft: '0px' },
  md: { width: '34px', height: '20px', thumb: '20px', checkedLeft: '-6px' },
  lg: { width: '48px', height: '24px', thumb: '24px', checkedLeft: '0px' },
} as const

type ToggleSize = keyof typeof toggleSizes

export interface ToggleProps extends Omit<
  React.ComponentProps<typeof ChakraSwitch.Root>,
  'children' | 'asChild' | 'value'
> {
  children?: React.ReactNode
  inputProps?: React.InputHTMLAttributes<HTMLInputElement>
  labelProps?: React.ComponentProps<typeof ChakraSwitch.Label>
  rootRef?: React.RefObject<HTMLLabelElement | null>
  invalid?: boolean
  required?: boolean
  value?: string | number
}

export const Toggle = React.forwardRef<HTMLInputElement, ToggleProps>(
  function Toggle(props, ref) {
    const {
      inputProps,
      labelProps,
      children,
      rootRef,
      checked,
      defaultChecked,
      onCheckedChange,
      disabled,
      readOnly,
      invalid,
      required,
      value,
      colorPalette,
      css: callerCss,
      size = 'md',
      ...rest
    } = props

    const sizeKey: ToggleSize =
      size === 'xs' || size === 'sm' || size === 'lg' ? size : 'md'
    const metrics = toggleSizes[sizeKey]
    const checkedTrackColor = colorPalette
      ? 'colorPalette.solid'
      : 'green.solid'
    const disabledCheckedTrackColor = colorPalette
      ? 'colorPalette.muted'
      : 'green.muted'

    const toggleCss = {
      '& [data-part="control"]': {
        backgroundColor: 'bg.emphasized',
      },
      '& [data-part="thumb"]': {
        backgroundColor: 'bg.panel',
      },
      '&[data-state="checked"]': {
        '& [data-part="control"]': {
          backgroundColor: checkedTrackColor,
        },
        '& [data-part="thumb"]': {
          backgroundColor: 'bg.panel',
          left: metrics.checkedLeft,
        },
      },
      '&[data-disabled]': {
        opacity: 1,
        '& [data-part="control"]': {
          backgroundColor: 'border.emphasized',
        },
        '& [data-part="thumb"]': {
          backgroundColor: 'bg.emphasized',
        },
      },
      '&[data-disabled][data-state="checked"]': {
        '& [data-part="control"]': {
          backgroundColor: disabledCheckedTrackColor,
        },
        '& [data-part="thumb"]': {
          backgroundColor: 'bg.panel',
        },
      },
    }

    return (
      <ChakraSwitch.Root
        ref={rootRef}
        checked={checked}
        defaultChecked={defaultChecked}
        onCheckedChange={onCheckedChange}
        disabled={disabled}
        readOnly={readOnly}
        invalid={invalid}
        required={required}
        value={value != null ? String(value) : undefined}
        display="flex"
        size={sizeKey}
        colorPalette={colorPalette}
        css={callerCss ? [toggleCss, callerCss] : toggleCss}
        {...rest}
      >
        <ChakraSwitch.HiddenInput ref={ref} {...inputProps} />
        <ChakraSwitch.Control
          width={metrics.width}
          height={metrics.height}
          padding="2px"
          justifyContent="flex-end"
          alignItems="center"
        >
          <ChakraSwitch.Thumb
            width={metrics.thumb}
            height={metrics.thumb}
            position="absolute"
            left="0px"
          />
        </ChakraSwitch.Control>
        {children != null && (
          <ChakraSwitch.Label {...labelProps}>{children}</ChakraSwitch.Label>
        )}
      </ChakraSwitch.Root>
    )
  },
)
